import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function Units() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterProp, setFilterProp] = useState('all');
  const defaultForm = { property_id: '', name: '', unit_number: '', bedrooms: 1, bathrooms: 1, max_guests: 2, base_price: 0, cleaning_fee: 0, status: 'available', amenities: [] };
  const [form, setForm] = useState(defaultForm);

  const fetchData = async () => {
    try {
      const [u, p] = await Promise.all([api.get('/units'), api.get('/properties')]);
      setUnits(u.data);
      setProperties(p.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), max_guests: Number(form.max_guests), base_price: Number(form.base_price), cleaning_fee: Number(form.cleaning_fee) };
      if (editing) await api.put(`/units/${editing.id}`, payload);
      else await api.post('/units', payload);
      setDialogOpen(false); setEditing(null); setForm(defaultForm); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (u) => {
    setEditing(u);
    setForm({ property_id: u.property_id || '', name: u.name, unit_number: u.unit_number || '', bedrooms: u.bedrooms || 1, bathrooms: u.bathrooms || 1, max_guests: u.max_guests || 2, base_price: u.base_price || 0, cleaning_fee: u.cleaning_fee || 0, status: u.status || 'available', amenities: u.amenities || [] });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this unit?')) return;
    await api.delete(`/units/${id}`); fetchData();
  };

  const filtered = filterProp === 'all' ? units : units.filter(u => u.property_id === filterProp);
  const statusColors = { available: 'bg-emerald-50 text-emerald-700 border-emerald-200', unavailable: 'bg-slate-100 text-slate-600', maintenance: 'bg-amber-50 text-amber-700 border-amber-200' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="units-page">
      <div className="page-header">
        <h1 className="page-title">Units</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Select value={filterProp} onValueChange={setFilterProp}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-property-select"><SelectValue placeholder="All Properties" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm(defaultForm); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-unit-btn"><Plus className="w-4 h-4 mr-2" />Add Unit</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Unit' : 'Add Unit'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div>
                  <Label>Property</Label>
                  <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                    <SelectTrigger data-testid="unit-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                    <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="unit-name-input" /></div>
                  <div><Label>Unit Number</Label><Input value={form.unit_number} onChange={e => setForm({ ...form, unit_number: e.target.value })} data-testid="unit-number-input" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Bedrooms</Label><Input type="number" value={form.bedrooms} onChange={e => setForm({ ...form, bedrooms: e.target.value })} data-testid="unit-bedrooms-input" /></div>
                  <div><Label>Bathrooms</Label><Input type="number" step="0.5" value={form.bathrooms} onChange={e => setForm({ ...form, bathrooms: e.target.value })} data-testid="unit-bathrooms-input" /></div>
                  <div><Label>Max Guests</Label><Input type="number" value={form.max_guests} onChange={e => setForm({ ...form, max_guests: e.target.value })} data-testid="unit-maxguests-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Base Price</Label><Input type="number" step="0.01" value={form.base_price} onChange={e => setForm({ ...form, base_price: e.target.value })} data-testid="unit-price-input" /></div>
                  <div><Label>Cleaning Fee</Label><Input type="number" step="0.01" value={form.cleaning_fee} onChange={e => setForm({ ...form, cleaning_fee: e.target.value })} data-testid="unit-cleaning-fee-input" /></div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="unit-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="unavailable">Unavailable</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="unit-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="table-wrap">
        <table className="w-full data-table min-w-[700px]">
          <thead><tr><th>Name</th><th>Property</th><th>Beds</th><th>Baths</th><th>Guests</th><th>Price</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="cursor-pointer" onClick={() => navigate(`/units/${u.id}`)} data-testid={`unit-row-${u.id}`}>
                <td className="font-medium text-slate-900">{u.name}</td>
                <td>{u.property_name}</td>
                <td>{u.bedrooms}</td>
                <td>{u.bathrooms}</td>
                <td>{u.max_guests}</td>
                <td>{u.base_price ? `R${u.base_price.toLocaleString()}` : 'N/A'}</td>
                <td><Badge className={statusColors[u.status] || ''}>{u.status}</Badge></td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} data-testid={`edit-unit-${u.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-unit-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No units found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
